import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import Logo from "@/components/app/Logo";

export default function NfcProgram() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/nfc/program/${token}`);
        setInfo(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Link inválido");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-nardo-50 flex items-center justify-center p-6">
        <div className="bg-white border border-nardo-200 rounded-md p-10 max-w-md text-center">
          <h2 className="font-display text-2xl text-navy mb-2">Link inválido</h2>
          <p className="text-sm text-nardo-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center text-nardo-500 uppercase tracking-[0.3em] text-xs">Cargando…</div>;
  }

  const isTeacher = info.kind === "teacher";
  const photoUrl = info.has_photo ? `${API}/nfc/program/${token}/photo` : null;

  return (
    <div className="min-h-screen bg-nardo-50">
      <div className="h-[3px] w-full bg-navy" />
      <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Logo />
        <span className="text-[11px] sm:text-xs font-mono text-nardo-500 tracking-tight" data-testid="brand-domain">uaicardaccess.emergentagent.com</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-20" data-testid="nfc-program-page">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-6 flex items-center gap-2">
          <span className="w-8 h-px bg-navy" /> Información personal
        </div>

        {/* Datos de la persona */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-8">
          {photoUrl && !imgError ? (
            <img
              src={photoUrl}
              alt={info.full_name}
              onError={() => setImgError(true)}
              className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl object-cover border border-nardo-200 shrink-0"
              data-testid="person-photo"
            />
          ) : (
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl bg-nardo-100 flex items-center justify-center text-navy font-display font-extrabold text-4xl border border-nardo-200 shrink-0" data-testid="person-photo-placeholder">
              {info.full_name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="text-center sm:text-left">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy" data-testid="person-name">
              {info.full_name}
            </h1>
            <div className="mt-3 space-y-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">{isTeacher ? "Cargo" : "Grado / Grupo"}</div>
              <div className="text-navy font-medium" data-testid="person-grade">{info.grade || "—"}</div>
            </div>
            <div className="text-sm text-nardo-500 mt-2">{info.school_name}</div>
          </div>
        </div>

        {/* UID asignado (solo lectura) */}
        <div className="bg-white border border-nardo-200 rounded-md p-8 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500 mb-4">UID asignado</div>
          <div
            data-testid="program-uid-display"
            className="w-full text-center text-2xl font-mono tracking-[0.3em] bg-nardo-50 border-2 border-dashed border-nardo-300 p-6 rounded-lg uppercase text-navy break-all"
          >
            {info.nfc_uid || "Sin UID asignado"}
          </div>
        </div>

        <div className="mt-6 text-xs text-nardo-500 leading-relaxed" data-testid="program-footer-note">
          Este link es único para <strong>{info.full_name}</strong>.
        </div>
      </main>
    </div>
  );
}
